import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PlanTier } from "@/types/plan";
import { BrandPanel } from "@/components/auth/brand-panel";
import { Logo } from "@/components/brand/logo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { registerRequest } from "@/features/auth/api";

const schema = z.object({
  shopName: z.string().min(1, "Dükkan adı gerekli.").max(200),
  shopPhone: z
    .string()
    .min(11, "Geçerli bir telefon numarası girin.")
    .max(11, "Geçerli bir telefon numarası girin."),
  shopAddress: z.string().max(500).optional().or(z.literal("")),
  shopTaxNumber: z.string().max(50).optional().or(z.literal("")),
  ownerFirstName: z.string().min(1, "Ad gerekli.").max(100),
  ownerLastName: z.string().min(1, "Soyad gerekli.").max(100),
  ownerEmail: z.string().email("Geçerli bir e-posta girin."),
  ownerPassword: z.string().min(8, "Parola en az 8 karakter olmalı.").max(100),
  planTier: z.enum(["Baslangic", "Profesyonel", "Kurumsal", "Elit"] as const),
});

interface PlanChoice {
  tier: PlanTier;
  name: string;
  price: string;
  blurb: string;
  highlighted?: boolean;
}

const planChoices: PlanChoice[] = [
  { tier: "Baslangic", name: "Başlangıç", price: "749 ₺/ay", blurb: "Tek kişilik atölye" },
  { tier: "Profesyonel", name: "Profesyonel", price: "1.799 ₺/ay", blurb: "En tercih edilen", highlighted: true },
  { tier: "Kurumsal", name: "Kurumsal", price: "3.999 ₺/ay", blurb: "Çok şubeli" },
  { tier: "Elit", name: "Elit", price: "Teklif", blurb: "Franchise / özel" },
];

type FormValues = z.infer<typeof schema>;

export function RegisterPage() {
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      shopName: "",
      shopPhone: "",
      shopAddress: "",
      shopTaxNumber: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerEmail: "",
      ownerPassword: "",
      planTier: "Profesyonel",
    },
  });

  const mutation = useMutation({
    mutationFn: registerRequest,
    onSuccess: () => {
      form.reset();
    },
  });

  const submitted = mutation.isSuccess;

  return (
    <>
      <title>Hesap oluştur | Ustorio</title>
      <meta
        name="description"
        content="Ustorio'da ücretsiz hesap oluşturun. Atölyenizin dijital arşivini birkaç dakikada kullanmaya başlayın."
      />
      <link rel="canonical" href="https://ustorio.com.tr/register" />
    <div className="flex min-h-svh bg-white">
      <BrandPanel />

      <div className="flex flex-1 items-center justify-center overflow-y-auto px-6 py-12 lg:w-1/2 lg:px-10">
        <div className="w-full max-w-lg">
          <div className="mb-10 flex items-center justify-between lg:hidden">
            <Logo size="md" to="/" />
          </div>

          <div className="mb-8 space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-ink-900">
              Dükkanını kur
            </h1>
            <p className="text-sm text-ink-500">
              Birkaç dakika içinde atölyeni dijitalleştir. Ücretsiz başla.
            </p>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
              className="space-y-8"
            >
              <fieldset className="space-y-4">
                <legend className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-400">
                  Dükkan bilgileri
                </legend>
                <FormField
                  control={form.control}
                  name="shopName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dükkan adı *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Örn. Ahmet Usta Oto"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="shopTaxNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vergi no</FormLabel>
                      <FormControl>
                        <Input className="h-11" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="shopAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Adres</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Dükkan adresi"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </fieldset>

              <fieldset className="space-y-4">
                <legend className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-400">
                  Sahip bilgileri
                </legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="ownerFirstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ad *</FormLabel>
                        <FormControl>
                          <Input className="h-11" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="ownerLastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Soyad *</FormLabel>
                        <FormControl>
                          <Input className="h-11" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="shopPhone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Telefon *</FormLabel>
                        <FormControl>
                          <PhoneInput
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            className="h-11"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="ownerEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>E-posta *</FormLabel>
                        <FormControl>
                          <Input type="email" className="h-11" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="ownerPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Parola *</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="En az 8 karakter"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </fieldset>

              <fieldset className="space-y-3">
                <legend className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-400">
                  Deneme paketi
                </legend>
                <p className="text-xs text-ink-500">
                  Seçtiğin paketi 14 gün ücretsiz denersin. Kredi kartı
                  istemiyoruz; deneme sonunda otomatik ücret alınmaz.
                </p>
                <FormField
                  control={form.control}
                  name="planTier"
                  render={({ field }) => (
                    <FormItem>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {planChoices.map((p) => {
                          const selected = field.value === p.tier;
                          return (
                            <button
                              key={p.tier}
                              type="button"
                              onClick={() => field.onChange(p.tier)}
                              className={cn(
                                "relative flex flex-col rounded-xl border p-3 text-left transition-all",
                                selected
                                  ? "border-brand-500 bg-brand-50/50 ring-1 ring-brand-500/40"
                                  : "border-ink-200 hover:border-brand-200 hover:bg-ink-50",
                              )}
                            >
                              {p.highlighted ? (
                                <span className="absolute -top-2 right-2 inline-flex items-center gap-0.5 rounded-full bg-brand-600 px-1.5 py-0.5 text-[8px] font-bold uppercase text-white">
                                  <Star className="h-2 w-2 fill-current" />
                                </span>
                              ) : null}
                              <span
                                className={cn(
                                  "text-[10px] font-semibold uppercase tracking-wider",
                                  selected ? "text-brand-700" : "text-ink-500",
                                )}
                              >
                                {p.name}
                              </span>
                              <span
                                className={cn(
                                  "mt-1 text-sm font-bold",
                                  selected ? "text-brand-700" : "text-ink-900",
                                )}
                              >
                                {p.price}
                              </span>
                              <span className="mt-0.5 text-[10px] leading-tight text-ink-500">
                                {p.blurb}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </fieldset>

              <Button
                type="submit"
                size="lg"
                className="h-11 w-full"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Dükkanı oluştur
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </Form>

          <p className="mt-8 text-center text-sm text-ink-500">
            Zaten hesabın var mı?{" "}
            <Link
              to="/login"
              className="font-semibold text-ink-900 hover:text-brand-600"
            >
              Giriş yap
            </Link>
          </p>
        </div>
      </div>

      <Dialog
        open={submitted}
        onOpenChange={(open) => {
          // Modal kapatılırsa kullanıcı ana sayfaya alınır.
          if (!open) navigate("/", { replace: true });
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="items-center text-center">
            <div className="mb-2 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <DialogTitle className="text-2xl">Başvurun alındı</DialogTitle>
            <DialogDescription className="text-balance text-sm leading-relaxed text-ink-500">
              Başvurunuz alınmıştır. En kısa sürede onay süreciniz
              tamamlanıp tarafınıza e-posta ile bilgi verilecektir.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              type="button"
              size="lg"
              className="h-11 gap-2"
              onClick={() => navigate("/", { replace: true })}
            >
              Ana sayfaya dön
              <ArrowRight className="h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
}
